import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Menu, X, User, LogOut, Settings } from 'lucide-react';
import brandwImage from '@/assets/xReplAI-brandw.png';
import { WaitlistModal } from './WaitlistModal';
import { AuthModal } from './AuthModal';
import { useMaintenanceCTA } from '../hooks/useMaintenanceCTA';
import { useAuth } from '../context/AuthContext';
import { markExtensionSource } from '@/lib/extensionBridge';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { isWaitlistOpen, setIsWaitlistOpen, handleCTA } = useMaintenanceCTA();
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-open auth modal when ?auth=extension is in the URL
  useEffect(() => {
    if (searchParams.get('auth') === 'extension' && !isAuthenticated) {
      markExtensionSource();
      setIsAuthModalOpen(true);
    }
  }, [searchParams, isAuthenticated]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '/', type: 'route' },
    { label: 'For Sales', href: '/for-sales', type: 'route' },
    { label: 'For Recruiters', href: '/for-recruiters', type: 'route' },
    { label: 'For Job Seekers', href: '/for-jobseekers', type: 'route' },
    { label: 'Pricing', href: '/pricing', type: 'route' },
    { label: 'Enterprise', href: '/enterprise', type: 'route' }
  ];

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.type === 'hash') {
      // If we're on a different page, navigate to home first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation, then scroll
        setTimeout(() => {
          const element = document.querySelector(item.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 dark:bg-[#0A1628]/95 backdrop-blur-sm shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src={brandwImage}
              alt="xReplAI"
              className="h-8 lg:h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              item.type === 'route' ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-gray-700 dark:text-gray-300 hover:text-[#1E3A8A] dark:hover:text-[#60A5FA] transition-colors cursor-pointer"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className="text-gray-700 dark:text-gray-300 hover:text-[#1E3A8A] dark:hover:text-[#60A5FA] transition-colors cursor-pointer"
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  {user?.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt={user.name}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#1E3A8A] flex items-center justify-center">
                      <User size={16} className="text-white" />
                    </div>
                  )}
                  <span className="text-gray-700 dark:text-gray-300">{user?.name}</span>
                </button>

                                {/* User Dropdown */}
                                {isUserMenuOpen && (
                                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2">
                                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user?.email}</p>
                                    </div>
                                    <Link
                                      to="/account"
                                      onClick={() => setIsUserMenuOpen(false)}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                    >
                                      <Settings size={16} />
                                      My Account
                                    </Link>
                                    <button
                                      onClick={() => {
                                        logout();
                                        setIsUserMenuOpen(false);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                    >
                                      <LogOut size={16} />
                                      Sign Out
                                    </button>
                                  </div>
                                )}
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-[#1E3A8A] dark:hover:text-[#60A5FA] transition-colors cursor-pointer"
              >
                Sign In
              </button>
            )}
            <button 
              onClick={() => handleCTA(() => {
                window.open('https://chromewebstore.google.com/detail/aeemmefmpkgmokecemoedojagcecabmj', '_blank');
              })}
              className="px-6 py-2 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-all hover:shadow-lg hover:shadow-[#60A5FA]/20 cursor-pointer"
            >
              Install Extension
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 dark:text-gray-300"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-[#0A1628] border-t dark:border-gray-800">
          <nav className="px-4 py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              item.type === 'route' ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 dark:text-gray-300 hover:text-[#1E3A8A] dark:hover:text-[#60A5FA] transition-colors py-2"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className="text-left text-gray-700 dark:text-gray-300 hover:text-[#1E3A8A] dark:hover:text-[#60A5FA] transition-colors py-2"
                >
                  {item.label}
                </button>
              )
            ))}
            <div className="flex flex-col gap-3 pt-4 border-t dark:border-gray-800">
                            {isAuthenticated ? (
                              <>
                                <div className="px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user?.email}</p>
                                </div>
                                <Link
                                  to="/account"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg flex items-center justify-center gap-2"
                                >
                                  <Settings size={16} />
                                  My Account
                                </Link>
                                <button
                                  onClick={() => {
                                    logout();
                                    setIsMobileMenuOpen(false);
                                  }}
                                  className="px-4 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg flex items-center justify-center gap-2"
                                >
                                  <LogOut size={16} />
                                  Sign Out
                                </button>
                              </>
              ) : (
                <button
                  onClick={() => {
                    setIsAuthModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg"
                >
                  Sign In
                </button>
              )}
              <button 
                onClick={() => {
                  handleCTA(() => {
                    window.open('https://chromewebstore.google.com/detail/aeemmefmpkgmokecemoedojagcecabmj', '_blank');
                  });
                  setIsMobileMenuOpen(false);
                }}
                className="px-6 py-2 bg-[#1E3A8A] text-white rounded-lg"
              >
                Install Extension
              </button>
            </div>
          </nav>
        </div>
      )}

      <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} />
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </header>
  );
}
